import React from 'react';
import { ErrorBoundary } from 'react-error-boundary';
import { useNavigate } from 'react-router-dom';
import {
  CalendarDays,
  Clock3,
  Columns3,
  LayoutDashboard,
  Package,
  Puzzle,
  RefreshCcw,
  Waypoints,
} from 'lucide-react';
import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  Line,
  LineChart,
  Pie,
  PieChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';

import { Button } from '@/ui/button';
import { Card, CardContent } from '@/ui/card';
import { Input } from '@/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/ui/select';
import { cn } from '@/lib/utils';

import { ErrorFallback } from 'smart-react';
import {
  AUTEST_DASHBOARD_TILELAYOUT,
  DATE_RANGE_OPTIONS,
  REFRESH_INTERVAL,
} from '../../../../constants/applicationConstants';
import {
  getExecutionResultSummary,
  getExecutionTotal,
  getExecutionTypeData,
  getExecutionsByModule,
  getTotalCounts,
} from '../../Execution/Services/ExecutionService';

const DEFAULT_METRICS = {
  totalTests: 0,
  totalTestCases: 0,
  totalRunSets: 0,
  totalCommands: 0,
  totalApplicationFlows: 0,
};

const EXECUTION_TYPE_COLORS = {
  ADHOC: '#8F87FF',
  STRESS: '#5ED3C6',
  REGRESSION: '#FFB86B',
};

const PASS_COLOR = '#22C3EE';
const FAIL_COLOR = '#8B7CFF';
const SECONDARY_BAR = '#5DA8FF';
const GRID_COLOR = 'oklch(var(--border) / 0.35)';
const AXIS_COLOR = 'oklch(var(--muted-foreground))';
const DASHBOARD_FONT_FAMILY =
  'Geist, "Geist Fallback", ui-sans-serif, system-ui, -apple-system, "Segoe UI", Roboto, Arial, "Noto Sans", "Liberation Sans", sans-serif';
const DESIGN_GUIDE = {
  cardPadding: 'p-5',
  labelClass:
    'text-[11px] font-semibold uppercase tracking-[0.18em] text-muted-foreground',
  panelTitleClass:
    '!text-[14px] !leading-5 font-semibold tracking-normal text-foreground',
  bodyTextClass: 'text-[13px] leading-6 text-muted-foreground',
  metricValueClass:
    'text-[28px] font-semibold leading-none tracking-[-0.03em] text-foreground',
};

const formatDateToInputValue = (date) => date.toISOString().split('T')[0];

const formatDateToDisplayValue = (value) => {
  if (!value) {
    return 'Select date';
  }

  const parsedDate = new Date(`${value}T00:00:00`);

  if (Number.isNaN(parsedDate.getTime())) {
    return value;
  }

  return new Intl.DateTimeFormat('en-US', {
    month: 'short',
    day: '2-digit',
    year: 'numeric',
  }).format(parsedDate);
};

const getDefaultDateRange = () => {
  const today = new Date();
  const lastWeek = new Date(today);
  lastWeek.setDate(today.getDate() - 7);

  return {
    startDate: formatDateToInputValue(lastWeek),
    endDate: formatDateToInputValue(today),
  };
};

const safeParse = (value, fallback) => {
  if (!value) {
    return fallback;
  }

  try {
    return JSON.parse(value);
  } catch (error) {
    return fallback;
  }
};

const getStoredDateFilter = () => {
  if (typeof window === 'undefined') {
    return {
      range: '7',
      dates: getDefaultDateRange(),
    };
  }

  const storedValue = safeParse(
    window.localStorage.getItem(`${AUTEST_DASHBOARD_TILELAYOUT}_DATE_FILTER`),
    null,
  );

  return {
    range: storedValue?.range ?? '7',
    dates: storedValue?.dates ?? getDefaultDateRange(),
  };
};

const getStoredRefreshInterval = () => {
  if (typeof window === 'undefined') {
    return REFRESH_INTERVAL[1];
  }

  const storedValue = safeParse(
    window.localStorage.getItem(
      `${AUTEST_DASHBOARD_TILELAYOUT}_REFRESH_INTERVAL`,
    ),
    null,
  );

  if (!storedValue) {
    return REFRESH_INTERVAL[1];
  }

  return (
    REFRESH_INTERVAL.find((option) => option.value === storedValue?.value) ||
    REFRESH_INTERVAL.find((option) => option.label === storedValue?.label) ||
    REFRESH_INTERVAL.find((option) => option.label === storedValue?.text) ||
    REFRESH_INTERVAL[1]
  );
};

const toNumber = (value) => {
  const parsedValue = Number(value);
  return Number.isFinite(parsedValue) ? parsedValue : 0;
};

const clamp = (value, min, max) => Math.min(max, Math.max(min, value));

const compactNumberFormatter = new Intl.NumberFormat('en-US', {
  notation: 'compact',
  maximumFractionDigits: 1,
});

const formatCompactNumber = (value) => compactNumberFormatter.format(toNumber(value));

const truncateAxisLabel = (value, maxLength = 14) => {
  if (!value) {
    return '';
  }

  return value.length > maxLength ? `${value.slice(0, maxLength)}...` : value;
};

const DashboardSurface = ({ className, ...props }) => (
  <Card
    className={cn(
      'rounded-lg border border-border/40 bg-card shadow-none',
      className,
    )}
    {...props}
  />
);

const DashboardSummaryPill = ({ value, label, accent = false }) => (
  <div
    className={cn(
      'inline-flex items-center gap-1.5 rounded-full border px-3 py-1 text-[12px] font-medium',
      accent
        ? 'border-emerald-500/25 bg-emerald-500/10 text-emerald-700 dark:text-emerald-300'
        : 'border-border/60 bg-background/70 text-muted-foreground',
    )}
  >
    <span className={cn('font-semibold', accent ? 'text-emerald-700 dark:text-emerald-300' : 'text-foreground')}>
      {value}
    </span>
    <span>{label}</span>
  </div>
);

const DashboardDateField = ({ label, value, onChange }) => (
  <label className='flex min-w-[180px] flex-col gap-2'>
    <span className='text-[11px] font-semibold uppercase tracking-[0.08em] text-muted-foreground'>
      {label}
    </span>
    <div className='relative'>
      <div className='flex h-10 items-center gap-3 rounded-xl border border-border/60 bg-background/80 px-4 text-foreground shadow-none'>
        <CalendarDays className='h-4 w-4 shrink-0 text-muted-foreground' />
        <span className='text-[14px] font-medium'>
          {formatDateToDisplayValue(value)}
        </span>
      </div>
      <Input
        type='date'
        value={value}
        onChange={(event) => onChange(event.target.value)}
        className='absolute inset-0 h-10 w-full cursor-pointer border-0 bg-transparent opacity-0 shadow-none [color-scheme:light] dark:[color-scheme:dark]'
      />
    </div>
  </label>
);

const MetricCardSkeleton = () => (
  <DashboardSurface className='overflow-hidden rounded-lg border-border/40 bg-card'>
    <CardContent className='p-5'>
      <div className='flex items-start justify-between gap-4'>
        <div className='space-y-3'>
          <div className='h-3 w-24 animate-pulse rounded bg-muted' />
          <div className='h-8 w-20 animate-pulse rounded bg-muted' />
        </div>
        <div className='h-11 w-11 animate-pulse rounded-lg bg-muted' />
      </div>
      <div className='mt-4 h-3 w-40 animate-pulse rounded bg-muted' />
      <div className='mt-5 h-1.5 animate-pulse rounded-full bg-muted' />
    </CardContent>
  </DashboardSurface>
);

const StatCard = ({
  icon: Icon,
  label,
  value,
  helper,
  onClick,
  accentClassName,
  progressClassName,
  progressWidth,
}) => (
  <button
    type='button'
    onClick={onClick}
    className='w-full appearance-none border-0 bg-transparent p-0 text-left focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring/60'
  >
    <DashboardSurface className='relative h-full overflow-hidden rounded-lg border-border/40 bg-card transition-colors hover:border-border/60 hover:bg-card/95'>
      <div className='absolute inset-x-6 top-0 h-px bg-gradient-to-r from-transparent via-foreground/15 to-transparent opacity-70 dark:via-white/40' />
      <CardContent className='p-5'>
        <div className='flex items-start justify-between gap-4'>
          <div className='space-y-3'>
            <p className='!text-[11px] font-semibold uppercase tracking-[0.18em] text-slate-600 dark:text-muted-foreground'>
              {label}
            </p>
            <div className='!text-[28px] font-semibold leading-none tracking-[-0.03em] text-foreground'>
              {value}
            </div>
          </div>
          <span
            className={cn(
              'inline-flex h-11 w-11 items-center justify-center rounded-lg border border-slate-200 bg-slate-50 shadow-sm dark:border-border/45 dark:bg-background/55 dark:shadow-none',
              accentClassName,
            )}
          >
            <Icon className='h-5 w-5' />
          </span>
        </div>
        <p className='!mt-3 !text-[13px] leading-6 text-slate-600 dark:text-muted-foreground'>{helper}</p>
        <div className='mt-5 h-1.5 overflow-hidden rounded-full bg-border/35'>
          <div
            className={cn(
              'h-full rounded-full bg-gradient-to-r transition-[width] duration-700',
              progressClassName,
            )}
            style={{ width: progressWidth }}
          />
        </div>
      </CardContent>
    </DashboardSurface>
  </button>
);

const ChartCard = ({ title, description, action, children, className }) => (
  <DashboardSurface className={cn('overflow-hidden rounded-lg border-border/40 bg-card', className)}>
    <CardContent className='p-5'>
      <div className='flex flex-col gap-4 md:flex-row md:items-start md:justify-between'>
        <div>
          <div className='text-[14px] font-semibold text-foreground'>{title}</div>
          {description ? (
            <p className='mt-1 text-[13px] leading-4 text-muted-foreground'>{description}</p>
          ) : null}
        </div>
        {action}
      </div>
      <div className='mt-5'>{children}</div>
    </CardContent>
  </DashboardSurface>
);

const ChartTooltipContent = ({ active, payload, label }) => {
  if (!active || !payload?.length) {
    return null;
  }

  return (
    <div className='rounded-xl border border-border/50 bg-card/95 px-3 py-2 text-[12px] shadow-lg backdrop-blur-sm'>
      {label ? (
        <div className='mb-1 font-semibold text-foreground'>{label}</div>
      ) : null}
      <div className='space-y-1.5'>
        {payload.map((entry) => (
          <div key={entry.name} className='flex items-center gap-2'>
            <span
              className='h-2.5 w-2.5 rounded-full'
              style={{ backgroundColor: entry.color }}
            />
            <span className='text-muted-foreground'>{entry.name}</span>
            <span className='ml-auto font-medium text-foreground'>
              {entry.value}
            </span>
          </div>
        ))}
      </div>
    </div>
  );
};

const EmptyChartState = ({ title = 'No data', description, className }) => (
  <div
    className={cn(
      'flex h-[280px] items-center justify-center rounded-2xl border border-dashed border-border/30 bg-background/10 px-6 text-center',
      className,
    )}
  >
    <div>
      <div className='text-[14px] font-semibold text-foreground'>{title}</div>
      {description ? (
        <div className='mt-2 text-[13px] text-muted-foreground'>{description}</div>
      ) : null}
    </div>
  </div>
);

const AutestDashboard = () => {
  const navigate = useNavigate();
  const storedDateFilter = React.useMemo(() => getStoredDateFilter(), []);
  const storedInterval = React.useMemo(() => getStoredRefreshInterval(), []);

  const [selectedRange, setSelectedRange] = React.useState(storedDateFilter.range);
  const [dates, setDates] = React.useState(storedDateFilter.dates);
  const [selectedInterval, setSelectedInterval] = React.useState(storedInterval);
  const [refreshTick, setRefreshTick] = React.useState(0);
  const [isLoading, setIsLoading] = React.useState(true);
  const [metrics, setMetrics] = React.useState(DEFAULT_METRICS);
  const [executionTypeData, setExecutionTypeData] = React.useState([]);
  const [resultSummaryData, setResultSummaryData] = React.useState([]);
  const [executionTotalData, setExecutionTotalData] = React.useState([]);
  const [modulePerformanceData, setModulePerformanceData] = React.useState([]);

  const updateSelectedDate = React.useCallback((nextDates, range) => {
    if (typeof window === 'undefined') {
      return;
    }

    window.localStorage.setItem(
      `${AUTEST_DASHBOARD_TILELAYOUT}_DATE_FILTER`,
      JSON.stringify({ range, dates: nextDates }),
    );
  }, []);

  React.useEffect(() => {
    if (typeof window === 'undefined') {
      return;
    }

    window.localStorage.setItem(
      `${AUTEST_DASHBOARD_TILELAYOUT}_REFRESH_INTERVAL`,
      JSON.stringify(selectedInterval),
    );
  }, [selectedInterval]);

  React.useEffect(() => {
    if (selectedInterval?.value === 'off') {
      return undefined;
    }

    const intervalInMinutes = Number.parseInt(selectedInterval?.value, 10);
    if (!Number.isFinite(intervalInMinutes)) {
      return undefined;
    }

    const intervalId = window.setInterval(() => {
      setRefreshTick((previousValue) => previousValue + 1);
    }, intervalInMinutes * 60 * 1000);

    return () => window.clearInterval(intervalId);
  }, [selectedInterval]);

  React.useEffect(() => {
    let ignore = false;

    const loadDashboardData = async () => {
      setIsLoading(true);

      try {
        const requestData = { ...dates };
        const [
          totalsResult,
          executionTypeResult,
          resultSummaryResult,
          executionTotalResult,
          moduleResult,
        ] = await Promise.allSettled([
          getTotalCounts({
            moduleName: 'AutestSummary',
            isServiceWorker: false,
            data: {},
          }),
          getExecutionTypeData({
            moduleName: 'AutestSummary',
            isServiceWorker: false,
            data: requestData,
          }),
          getExecutionResultSummary({
            moduleName: 'AutestSummary',
            isServiceWorker: false,
            data: requestData,
          }),
          getExecutionTotal({
            moduleName: 'AutestSummary',
            isServiceWorker: false,
            data: requestData,
          }),
          getExecutionsByModule({
            moduleName: 'StatusSummary',
            isServiceWorker: false,
            data: requestData,
          }),
        ]);

        if (ignore) {
          return;
        }

        const totalsPayload =
          totalsResult.status === 'fulfilled' ? totalsResult.value?.analyticsData : null;

        setMetrics({
          totalTests: toNumber(totalsPayload?.TotalTests),
          totalTestCases: toNumber(totalsPayload?.TotalTestCases),
          totalRunSets: toNumber(totalsPayload?.TotalRunSets),
          totalCommands: toNumber(totalsPayload?.TotalCommands),
          totalApplicationFlows: toNumber(totalsPayload?.TotalApplicationFlows),
        });

        setExecutionTypeData(
          executionTypeResult.status === 'fulfilled'
            ? executionTypeResult.value?.invokeMethodTests ?? []
            : [],
        );
        setResultSummaryData(
          resultSummaryResult.status === 'fulfilled'
            ? resultSummaryResult.value?.executionsPassFail ?? []
            : [],
        );
        setExecutionTotalData(
          executionTotalResult.status === 'fulfilled'
            ? executionTotalResult.value?.ExecutionTotal ?? []
            : [],
        );
        setModulePerformanceData(
          moduleResult.status === 'fulfilled'
            ? moduleResult.value?.executionsByModule ?? []
            : [],
        );
      } catch (error) {
        if (!ignore) {
          console.error('Error loading Autest dashboard:', error);
          setMetrics(DEFAULT_METRICS);
          setExecutionTypeData([]);
          setResultSummaryData([]);
          setExecutionTotalData([]);
          setModulePerformanceData([]);
        }
      } finally {
        if (!ignore) {
          setIsLoading(false);
        }
      }
    };

    void loadDashboardData();

    return () => {
      ignore = true;
    };
  }, [dates.endDate, dates.startDate, refreshTick]);

  const updateDateField = (field, value) => {
    const nextDates = {
      ...dates,
      [field]: value,
    };

    setSelectedRange('custom');
    setDates(nextDates);
    updateSelectedDate(nextDates, 'custom');
  };

  const handleRangeChange = (value) => {
    setSelectedRange(value);

    if (value === 'custom') {
      updateSelectedDate(dates, value);
      return;
    }

    const today = new Date();
    const nextStartDate = new Date(today);

    if (value === '14') {
      nextStartDate.setDate(today.getDate() - 14);
    } else if (value === '30') {
      nextStartDate.setDate(today.getDate() - 30);
    } else {
      nextStartDate.setDate(today.getDate() - 7);
    }

    const nextDates = {
      startDate: formatDateToInputValue(nextStartDate),
      endDate: formatDateToInputValue(today),
    };

    setDates(nextDates);
    updateSelectedDate(nextDates, value);
  };

  const handleIntervalChange = (value) => {
    const nextInterval =
      REFRESH_INTERVAL.find((option) => option.value === value) ||
      selectedInterval;

    setSelectedInterval(nextInterval);
  };

  const selectedRangeLabel =
    DATE_RANGE_OPTIONS.find((option) => option.value === selectedRange)?.text ||
    'Last 7 Days';
  const selectedRangeControlLabel =
    selectedRange === 'custom' ? 'Custom Range' : selectedRangeLabel;

  const executionMixData = React.useMemo(() => {
    const requiredTypes = ['ADHOC', 'STRESS', 'REGRESSION'];
    const sourceMap = new Map(
      executionTypeData.map((item) => [
        item?.ExecutionMethod || 'Unknown',
        toNumber(item?.Count),
      ]),
    );

    const seededTypes = requiredTypes.map((type) => ({
      name: type,
      value: sourceMap.get(type) ?? 0,
      color: EXECUTION_TYPE_COLORS[type],
    }));

    const additionalTypes = executionTypeData
      .filter((item) => !requiredTypes.includes(item?.ExecutionMethod))
      .map((item, index) => ({
        name: item?.ExecutionMethod || `Type ${index + 1}`,
        value: toNumber(item?.Count),
        color: ['#A78BFA', '#7DD3FC', '#F9A8D4', '#FCD34D'][index % 4],
      }));

    return [...seededTypes, ...additionalTypes].filter((item) => item.value > 0);
  }, [executionTypeData]);

  const executionResultsTrend = React.useMemo(
    () =>
      resultSummaryData.map((item, index) => ({
        label: item?.Period || `Period ${index + 1}`,
        passed: toNumber(item?.Passed),
        failed: toNumber(item?.Failed),
      })),
    [resultSummaryData],
  );

  const executionTotalsByType = React.useMemo(
    () =>
      executionTotalData.map((item, index) => ({
        label: item?.ExecutionType || `Type ${index + 1}`,
        passed: toNumber(item?.Passed),
        failed: toNumber(item?.Failed),
      })),
    [executionTotalData],
  );

  const modulesChartData = React.useMemo(
    () =>
      [...modulePerformanceData]
        .map((item, index) => ({
          name: item?.Module || `Module ${index + 1}`,
          passed: toNumber(item?.Passed),
          failed: toNumber(item?.Failed),
          total: toNumber(item?.Passed) + toNumber(item?.Failed),
        }))
        .sort((firstItem, secondItem) => secondItem.total - firstItem.total)
        .slice(0, 6),
    [modulePerformanceData],
  );

  const totalPassed = React.useMemo(
    () =>
      executionResultsTrend.reduce(
        (sum, item) => sum + item.passed,
        0,
      ),
    [executionResultsTrend],
  );
  const totalFailed = React.useMemo(
    () =>
      executionResultsTrend.reduce(
        (sum, item) => sum + item.failed,
        0,
      ),
    [executionResultsTrend],
  );
  const totalTrackedExecutions = totalPassed + totalFailed;
  const passRate = totalTrackedExecutions
    ? Math.round((totalPassed / totalTrackedExecutions) * 100)
    : 0;

  const passFailData = React.useMemo(
    () => [
      { name: 'Passed', value: totalPassed, color: PASS_COLOR },
      { name: 'Failed', value: totalFailed, color: FAIL_COLOR },
    ].filter((item) => item.value > 0),
    [totalPassed, totalFailed],
  );

  const topExecutionType = React.useMemo(
    () =>
      executionMixData.reduce(
        (bestItem, currentItem) =>
          currentItem.value > bestItem.value ? currentItem : bestItem,
        { name: 'No data', value: 0 },
      ),
    [executionMixData],
  );

  const totalExecutionMix = React.useMemo(
    () =>
      executionMixData.reduce(
        (sum, item) => sum + item.value,
        0,
      ),
    [executionMixData],
  );

  const topModule = React.useMemo(
    () =>
      modulesChartData.reduce(
        (bestItem, currentItem) =>
          currentItem.total > bestItem.total ? currentItem : bestItem,
        { name: 'No module', total: 0, passed: 0, failed: 0 },
      ),
    [modulesChartData],
  );

  const topSummaryPills = [
    {
      value: formatCompactNumber(metrics.totalTests),
      label: 'active tests',
      accent: true,
    },
    {
      value: formatCompactNumber(metrics.totalTestCases),
      label: 'test cases in scope',
    },
    {
      value: formatCompactNumber(metrics.totalApplicationFlows),
      label: 'tracked flows',
    },
  ];

  const statCards = [
    {
      icon: LayoutDashboard,
      label: 'Total tests',
      value: metrics.totalTests,
      helper: `${formatCompactNumber(metrics.totalRunSets)} run sets linked`,
      accentClassName: 'text-sky-600 dark:text-sky-300',
      progressClassName: 'from-sky-400 via-sky-400 to-primary/80',
      progressWidth: `${clamp(
        Math.round((toNumber(metrics.totalTests) / Math.max(toNumber(metrics.totalTestCases), 1)) * 100),
        14,
        100,
      )}%`,
      onClick: () => navigate('/autest/tests', { state: { isBack: true } }),
    },
    {
      icon: Columns3,
      label: 'Test cases',
      value: metrics.totalTestCases,
      helper: `${topExecutionType.name} is leading this range`,
      accentClassName: 'text-violet-600 dark:text-violet-300',
      progressClassName: 'from-violet-400 via-violet-400 to-primary/80',
      progressWidth: `${clamp(
        Math.round((toNumber(metrics.totalTestCases) / Math.max(toNumber(metrics.totalTests), 1)) * 100),
        28,
        100,
      )}%`,
      onClick: () => navigate('/autest/testcases', { state: { isBack: true } }),
    },
    {
      icon: Waypoints,
      label: 'Run sets',
      value: metrics.totalRunSets,
      helper: `${formatCompactNumber(totalTrackedExecutions)} tracked outcomes`,
      accentClassName: 'text-cyan-600 dark:text-cyan-300',
      progressClassName: 'from-cyan-400 via-emerald-400 to-primary/80',
      progressWidth: `${clamp(
        Math.round((toNumber(metrics.totalRunSets) / Math.max(totalTrackedExecutions, 1)) * 100),
        20,
        100,
      )}%`,
      onClick: () => navigate('/autest/run-sets', { state: { isBack: true } }),
    },
    {
      icon: Puzzle,
      label: 'Commands',
      value: metrics.totalCommands,
      helper: `${formatCompactNumber(metrics.totalApplicationFlows)} flows connected`,
      accentClassName: 'text-indigo-600 dark:text-indigo-300',
      progressClassName: 'from-indigo-400 via-violet-400 to-primary/80',
      progressWidth: `${clamp(
        Math.round((toNumber(metrics.totalCommands) / Math.max(toNumber(metrics.totalApplicationFlows), 1)) * 14),
        24,
        100,
      )}%`,
      onClick: () => navigate('/autest/commands', { state: { isBack: true } }),
    },
    {
      icon: Package,
      label: 'Application flows',
      value: metrics.totalApplicationFlows,
      helper: `${topModule.name} is the busiest module`,
      accentClassName: 'text-amber-600 dark:text-amber-300',
      progressClassName: 'from-amber-400 via-amber-400 to-violet-400',
      progressWidth: `${clamp(
        Math.round((toNumber(metrics.totalApplicationFlows) / Math.max(toNumber(metrics.totalCommands), 1)) * 100),
        18,
        100,
      )}%`,
      onClick: () => navigate('/autest/flows', { state: { isBack: true } }),
    },
  ];

  return (
    <ErrorBoundary FallbackComponent={ErrorFallback}>
      <div className='tw dashboard-theme' style={{ fontFamily: DASHBOARD_FONT_FAMILY }}>
        <div className='tw-container flex w-full flex-col gap-5 py-5 text-[13px] text-foreground sm:py-6 [&_a]:cursor-pointer [&_button]:cursor-pointer'>
          <section className='overflow-visible pt-1 text-foreground'>
            <div className='w-full space-y-1'>
              <div className='!mb-0 !text-[30px] font-medium leading-none tracking-tight text-foreground sm:!text-[32px]'>
                Dashboard
              </div>
              <p className='!mt-3 max-w-3xl text-[13px] leading-6 text-muted-foreground'>
                Monitor execution volume, module coverage, outcome trends, and
                activity from one workspace.
              </p>
            </div>

            <div className='mt-6 flex flex-col gap-6 xl:flex-row xl:items-end xl:justify-between'>
              <div className='min-w-0 space-y-4'>
                <div className='flex flex-wrap items-center gap-2'>
                  {topSummaryPills.map((item) => (
                    <DashboardSummaryPill key={item.label} {...item} />
                  ))}
                </div>
              </div>

              <div className='flex w-full flex-col gap-3 xl:ml-auto xl:w-auto xl:items-end'>
                {selectedRange === 'custom' ? (
                  <div className='grid w-full gap-3 sm:grid-cols-2 sm:justify-end xl:w-auto'>
                    <DashboardDateField
                      label='Start Date'
                      value={dates.startDate}
                      onChange={(value) => updateDateField('startDate', value)}
                    />

                    <DashboardDateField
                      label='End Date'
                      value={dates.endDate}
                      onChange={(value) => updateDateField('endDate', value)}
                    />
                  </div>
                ) : null}

                <div className='grid w-full grid-cols-[minmax(0,1fr)_40px_40px] gap-2 sm:ml-auto sm:w-fit sm:grid-cols-[220px_40px_40px] xl:items-end'>
                  <label className='flex min-w-0 flex-col gap-2 xl:min-w-[220px]'>
                    {selectedRange === 'custom' ? (
                      <span className='text-[11px] font-semibold uppercase tracking-[0.08em] text-muted-foreground'>
                        Range
                      </span>
                    ) : (
                      <span className='h-[18px]' />
                    )}
                    <Select value={selectedRange} onValueChange={handleRangeChange}>
                      <SelectTrigger className='h-10 rounded-xl border-border/60 bg-background/80 px-4 text-[14px] text-foreground shadow-none [&>svg]:text-muted-foreground'>
                        <div className='flex min-w-0 items-center gap-3'>
                          <CalendarDays className='h-4 w-4 shrink-0 text-muted-foreground' />
                          <SelectValue placeholder='Select range'>
                            {selectedRangeControlLabel}
                          </SelectValue>
                        </div>
                      </SelectTrigger>
                      <SelectContent>
                        {DATE_RANGE_OPTIONS.map((option) => (
                          <SelectItem key={option.value} value={option.value}>
                            {option.value === 'custom' ? 'Custom Range' : option.text}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </label>

                  <label className='flex min-w-[40px] self-end flex-col gap-2'>
                    <span className='h-[18px]' aria-hidden='true' />
                    <Select
                      value={selectedInterval?.value ?? 'off'}
                      onValueChange={handleIntervalChange}
                    >
                      <SelectTrigger
                        aria-label={`Refresh cadence: ${selectedInterval?.text || selectedInterval?.label || 'Off'}`}
                        className='h-10 w-10 rounded-xl border-border/60 bg-background/80 px-0 text-foreground shadow-none justify-center [&>svg]:text-muted-foreground [&>svg:last-child]:hidden'
                      >
                        <div className='flex min-w-0 items-center justify-center'>
                          <Clock3 className='h-4 w-4 shrink-0 text-muted-foreground' />
                          <span className='sr-only'>
                            {selectedInterval?.text || selectedInterval?.label || 'Off'}
                          </span>
                        </div>
                      </SelectTrigger>
                      <SelectContent>
                        {REFRESH_INTERVAL.map((option) => (
                          <SelectItem key={option.value} value={option.value}>
                            {option.text || option.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </label>

                  <Button
                    type='button'
                    variant='outline'
                    size='icon'
                    onClick={() => setRefreshTick((previousValue) => previousValue + 1)}
                    className='h-10 w-10 self-end rounded-xl border-border/60 bg-background/80 text-foreground shadow-none hover:bg-accent hover:text-foreground'
                    aria-label='Refresh dashboard'
                  >
                    <RefreshCcw className={cn('h-4 w-4', isLoading && 'animate-spin')} />
                  </Button>
                </div>
              </div>
            </div>
          </section>

          <section className='grid grid-cols-1 gap-3 md:grid-cols-2 xl:grid-cols-5'>
            {isLoading
              ? Array.from({ length: 5 }).map((_, index) => (
                <MetricCardSkeleton key={index} />
              ))
              : statCards.map((item) => (
                <StatCard key={item.label} {...item} />
              ))}
          </section>

          <section className='grid grid-cols-1 gap-4 xl:grid-cols-[minmax(0,1fr)_336px]'>
            <ChartCard
              title='Execution Results by Period'
              description='Passed and failed outcomes across the selected time window.'
              action={
                <div className='flex flex-wrap items-center gap-4 text-[11px] text-muted-foreground'>
                  <span className='inline-flex items-center gap-2'>
                    <span className='h-[2px] w-4 rounded-full' style={{ backgroundColor: PASS_COLOR }} />
                    Passed
                  </span>
                  <span className='inline-flex items-center gap-2'>
                    <span className='h-[2px] w-4 rounded-full' style={{ backgroundColor: FAIL_COLOR }} />
                    Failed
                  </span>
                </div>
              }
            >
                <div className='rounded-2xl border border-dashed border-border/30 bg-background/10 p-4'>
                  {executionResultsTrend.length > 0 ? (
                    <div className='h-[300px]'>
                      <ResponsiveContainer width='100%' height='100%'>
                        <LineChart data={executionResultsTrend}>
                          <CartesianGrid stroke={GRID_COLOR} strokeDasharray='3 5' vertical={false} />
                          <XAxis
                            dataKey='label'
                            tick={{ fill: AXIS_COLOR, fontSize: 11, fontFamily: DASHBOARD_FONT_FAMILY }}
                          axisLine={false}
                          tickLine={false}
                          dy={8}
                        />
                        <YAxis
                          tick={{ fill: AXIS_COLOR, fontSize: 11, fontFamily: DASHBOARD_FONT_FAMILY }}
                          axisLine={false}
                          tickLine={false}
                          width={36}
                        />
                          <Tooltip
                            content={<ChartTooltipContent />}
                            cursor={{ fill: 'transparent' }}
                          />
                          <Line
                            type='monotone'
                            dataKey='passed'
                            name='Passed'
                            stroke={PASS_COLOR}
                            strokeWidth={3}
                            dot={{
                              r: 4,
                              strokeWidth: 2,
                              fill: 'oklch(var(--card))',
                              stroke: PASS_COLOR,
                            }}
                            activeDot={{
                              r: 6,
                              strokeWidth: 3,
                              fill: PASS_COLOR,
                              stroke: 'oklch(var(--card))',
                            }}
                          />
                          <Line
                            type='monotone'
                            dataKey='failed'
                            name='Failed'
                            stroke={FAIL_COLOR}
                            strokeWidth={3}
                            dot={{
                              r: 4,
                              strokeWidth: 2,
                              fill: 'oklch(var(--card))',
                              stroke: FAIL_COLOR,
                            }}
                            activeDot={{
                              r: 6,
                              strokeWidth: 3,
                              fill: FAIL_COLOR,
                              stroke: 'oklch(var(--card))',
                            }}
                          />
                        </LineChart>
                      </ResponsiveContainer>
                    </div>
                  ) : (
                  <EmptyChartState
                    title='No activity'
                    description='No execution results were returned for the selected date range.'
                    className='h-[300px] border-0 bg-transparent px-0'
                  />
                )}
              </div>
            </ChartCard>

            <ChartCard
              title='Pass vs fail'
              description='A quick read on overall automation health.'
            >
              <div className='flex min-h-[360px] flex-col'>
                <div className='flex flex-1 items-center justify-center'>
                  {passFailData.length > 0 ? (
                    <div className='relative h-[220px] w-full max-w-[240px]'>
                      <ResponsiveContainer width='100%' height='100%'>
                        <PieChart>
                          <Tooltip
                            content={<ChartTooltipContent />}
                            cursor={{ fill: 'transparent' }}
                          />
                          <Pie
                            data={passFailData}
                            dataKey='value'
                            nameKey='name'
                            innerRadius={52}
                            outerRadius={96}
                            paddingAngle={4}
                            cornerRadius={10}
                            strokeWidth={0}
                          >
                            {passFailData.map((item) => (
                              <Cell key={item.name} fill={item.color} />
                            ))}
                          </Pie>
                        </PieChart>
                      </ResponsiveContainer>
                    </div>
                  ) : (
                    <EmptyChartState
                      title='No results'
                      description='No pass or fail results were returned for the selected date range.'
                      className='h-[240px] w-full border-0 bg-transparent px-0'
                    />
                  )}
                </div>

                {passFailData.length > 0 ? (
                  <div className='mt-3 space-y-3'>
                    {passFailData.map((item) => {
                      const share = totalTrackedExecutions
                        ? ((item.value / totalTrackedExecutions) * 100).toFixed(1)
                        : '0.0';

                      return (
                        <div
                          key={item.name}
                          className='flex items-center justify-between gap-3'
                        >
                          <div className='flex min-w-0 items-center gap-3'>
                            <span
                              className='h-2.5 w-2.5 shrink-0 rounded-[3px]'
                              style={{ backgroundColor: item.color }}
                            />
                            <span className='truncate text-[13px] text-muted-foreground'>
                              {item.name}
                            </span>
                          </div>
                          <span className='shrink-0 text-[11px] font-medium text-muted-foreground'>
                            {share}%
                          </span>
                        </div>
                      );
                    })}
                  </div>
                ) : null}
              </div>
            </ChartCard>
          </section>

        <section className='grid gap-4 xl:grid-cols-2'>
          <ChartCard
            title='Executions by Type'
            description='Method distribution across the selected range.'
          >
            {executionMixData.length > 0 ? (
              <div className='flex min-h-[360px] flex-col'>
                <div className='flex flex-1 items-center justify-center'>
                  <div className='relative h-[220px] w-full max-w-[240px]'>
                    <ResponsiveContainer width='100%' height='100%'>
                      <PieChart>
                        <Tooltip
                          content={<ChartTooltipContent />}
                          cursor={{ fill: 'transparent' }}
                        />
                        <Pie
                          data={executionMixData}
                          dataKey='value'
                          nameKey='name'
                          innerRadius={52}
                          outerRadius={96}
                          paddingAngle={4}
                          cornerRadius={10}
                          strokeWidth={0}
                        >
                          {executionMixData.map((item) => (
                            <Cell key={item.name} fill={item.color} />
                          ))}
                        </Pie>
                      </PieChart>
                    </ResponsiveContainer>
                  </div>
                </div>

                <div className='mt-3 space-y-3'>
                  {executionMixData.slice(0, 4).map((item) => {
                    const share = totalExecutionMix
                      ? ((item.value / totalExecutionMix) * 100).toFixed(1)
                      : '0.0';

                    return (
                      <div
                        key={item.name}
                        className='flex items-center justify-between gap-3'
                      >
                        <div className='flex min-w-0 items-center gap-3'>
                          <span
                            className='h-2.5 w-2.5 shrink-0 rounded-[3px]'
                            style={{ backgroundColor: item.color }}
                          />
                          <span className='truncate text-[13px] text-muted-foreground'>
                            {item.name}
                          </span>
                        </div>
                        <span className='shrink-0 text-[11px] font-medium text-muted-foreground'>
                          {share}%
                        </span>
                      </div>
                    );
                  })}
                </div>
              </div>
            ) : (
              <EmptyChartState
                title='No execution types'
                description='No execution type data was returned for the selected date range.'
              />
            )}
          </ChartCard>

          <ChartCard
            title='Test Executions'
            description='Passed and failed counts grouped by execution type.'
            action={
              <div className='flex flex-wrap items-center gap-4 text-[11px] text-muted-foreground'>
                <span className='inline-flex items-center gap-2'>
                  <span className='h-[2px] w-4 rounded-full' style={{ backgroundColor: SECONDARY_BAR }} />
                  Passed
                </span>
                <span className='inline-flex items-center gap-2'>
                  <span className='h-[2px] w-4 rounded-full' style={{ backgroundColor: FAIL_COLOR }} />
                  Failed
                </span>
              </div>
            }
          >
            {executionTotalsByType.length > 0 ? (
              <div className='rounded-2xl border border-dashed border-border/30 bg-background/10 p-4'>
                <div className='h-[300px]'>
                  <ResponsiveContainer width='100%' height='100%'>
                    <BarChart
                      data={executionTotalsByType}
                      barGap={10}
                      barCategoryGap='32%'
                    >
                      <CartesianGrid stroke={GRID_COLOR} strokeDasharray='3 5' vertical={false} />
                      <XAxis
                        dataKey='label'
                        tick={{ fill: AXIS_COLOR, fontSize: 11, fontFamily: DASHBOARD_FONT_FAMILY }}
                        axisLine={false}
                        tickLine={false}
                        dy={8}
                      />
                      <YAxis
                        tick={{ fill: AXIS_COLOR, fontSize: 11, fontFamily: DASHBOARD_FONT_FAMILY }}
                        axisLine={false}
                        tickLine={false}
                        width={36}
                      />
                      <Tooltip
                        content={<ChartTooltipContent />}
                        cursor={{ fill: 'transparent' }}
                      />
                      <Bar
                        dataKey='passed'
                        name='Passed'
                        fill={SECONDARY_BAR}
                        barSize={30}
                        radius={[8, 8, 0, 0]}
                      />
                      <Bar
                        dataKey='failed'
                        name='Failed'
                        fill={FAIL_COLOR}
                        barSize={30}
                        radius={[8, 8, 0, 0]}
                      />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </div>
            ) : (
              <EmptyChartState
                title='No execution totals'
                description='No execution totals were returned for the selected date range.'
              />
            )}
          </ChartCard>
        </section>

        <ChartCard
          title='Module Performance'
          description='Passed and failed results by module for the selected range.'
          action={
            <div className='flex flex-wrap items-center gap-4 text-[11px] text-muted-foreground'>
              <span className='inline-flex items-center gap-2'>
                <span className='h-[2px] w-4 rounded-full' style={{ backgroundColor: PASS_COLOR }} />
                Passed
              </span>
              <span className='inline-flex items-center gap-2'>
                <span className='h-[2px] w-4 rounded-full' style={{ backgroundColor: FAIL_COLOR }} />
                Failed
              </span>
            </div>
          }
        >
          {modulesChartData.length > 0 ? (
            <div className='rounded-2xl border border-dashed border-border/30 bg-background/10 p-4'>
              <div className='h-[320px]'>
                <ResponsiveContainer width='100%' height='100%'>
                  <BarChart
                    data={modulesChartData}
                    layout='vertical'
                    margin={{ top: 4, right: 8, bottom: 4, left: 4 }}
                    barGap={6}
                    barCategoryGap='24%'
                  >
                    <CartesianGrid stroke={GRID_COLOR} horizontal={false} />
                    <XAxis
                      type='number'
                      tick={{ fill: AXIS_COLOR, fontSize: 12, fontFamily: DASHBOARD_FONT_FAMILY }}
                      axisLine={false}
                      tickLine={false}
                    />
                    <YAxis
                      type='category'
                      dataKey='name'
                      width={110}
                      tick={{ fill: AXIS_COLOR, fontSize: 12, fontFamily: DASHBOARD_FONT_FAMILY }}
                      tickFormatter={(value) => truncateAxisLabel(value)}
                      axisLine={false}
                      tickLine={false}
                    />
                    <Tooltip
                      content={<ChartTooltipContent />}
                      cursor={{ fill: 'transparent' }}
                    />
                    <Bar
                      dataKey='passed'
                      name='Passed'
                      fill={PASS_COLOR}
                      radius={[0, 8, 8, 0]}
                      maxBarSize={16}
                    />
                    <Bar
                      dataKey='failed'
                      name='Failed'
                      fill={FAIL_COLOR}
                      radius={[0, 8, 8, 0]}
                      maxBarSize={16}
                    />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </div>
          ) : (
            <EmptyChartState
              title='No module performance'
              description='No module performance data was returned for the selected date range.'
            />
          )}
        </ChartCard>

          {isLoading ? (
            <div className='pointer-events-none fixed inset-x-0 bottom-6 z-10 flex justify-center'>
              <div className='rounded-full border border-border/70 bg-card px-4 py-2 shadow-lg'>
                <div className='flex items-center gap-2 text-[13px] text-muted-foreground'>
                  <RefreshCcw className='h-4 w-4 animate-spin' />
                  <span>Refreshing Autest dashboard</span>
                </div>
              </div>
            </div>
          ) : null}
        </div>
      </div>
    </ErrorBoundary>
  );
};

export default AutestDashboard;
